import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertBanner, Button, DataTable, Select, Tag, TextInput, useLocale, type DataTableColumn } from '@ministry/ui/internal';

import { listPlatformAuditEvents, type PlatformAuditEvent, type PlatformAuditFilter } from '../../lib/admin-platform.js';
import { formatInstantWithTime } from '../../lib/dates.js';
import { ApiError } from '../../lib/api-client.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { ForbiddenState } from '../../components/ForbiddenState.js';

const LIMIT_OPTIONS = [50, 100, 250, 500];

/**
 * SCREEN_SPECS.md I21/I18-adjacent — the cross-tenant audit log (`GET
 * /platform/audit-events`, `audit.read_platform`, `super_admin` only). AC-31: no write,
 * annotate, or delete endpoint exists for any role, so this screen is read + filter only,
 * never an edit affordance of any kind.
 *
 * The endpoint takes `limit` (capped at 500), not cursor/offset pagination — so a filter
 * bar is the way to narrow results, and the table names when it may be showing a
 * truncated window rather than implying it is complete.
 */
export function PlatformAuditPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [category, setCategory] = React.useState('');
  const [action, setAction] = React.useState('');
  const [occurredFrom, setOccurredFrom] = React.useState('');
  const [occurredTo, setOccurredTo] = React.useState('');
  const [limit, setLimit] = React.useState(100);
  const [filter, setFilter] = React.useState<PlatformAuditFilter>({ limit: 100 });

  const query = useQuery({
    queryKey: ['admin-platform-audit', filter],
    queryFn: ({ signal }) => listPlatformAuditEvents(filter, signal),
  });

  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('admin.audit.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const rows = query.data ?? [];

  const columns: DataTableColumn<PlatformAuditEvent>[] = [
    {
      key: 'occurredAt',
      header: t('admin.audit.column.occurredAt'),
      visibleFrom: 'always',
      cardSlot: 'title',
      // R-053: an audit row without a time of day cannot order two events on the
      // same day, which is most of what an audit log is for.
      render: (row) => formatInstantWithTime(row.occurredAt, locale),
    },
    {
      key: 'category',
      header: t('admin.audit.column.category'),
      visibleFrom: 'always',
      cardSlot: 'line2',
      render: (row) => row.category,
    },
    {
      key: 'action',
      header: t('admin.audit.column.action'),
      visibleFrom: 'always',
      cardSlot: 'line3',
      grow: true,
      render: (row) => row.action,
    },
    {
      key: 'outcome',
      header: t('admin.audit.column.outcome'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <Tag tone={row.outcome === 'success' ? 'neutral' : 'brand'}>{row.outcome}</Tag>,
    },
    {
      key: 'subject',
      header: t('admin.audit.column.subject'),
      visibleFrom: 'md',
      cardSlot: 'line4',
      render: (row) => row.subjectLabel ?? row.subjectType ?? '—',
    },
    {
      key: 'crossTenant',
      header: t('admin.audit.column.crossTenant'),
      visibleFrom: 'lg',
      render: (row) => (row.crossTenant ? t('admin.audit.crossTenantYes') : t('admin.audit.crossTenantNo')),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('admin.audit.title')} description={t('admin.audit.description')} />

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setFilter({
            category: category.trim() || undefined,
            action: action.trim() || undefined,
            occurredFrom: occurredFrom ? new Date(occurredFrom).toISOString() : undefined,
            occurredTo: occurredTo ? new Date(occurredTo).toISOString() : undefined,
            limit,
          });
        }}
      >
        <div className="w-44">
          <TextInput id="admin-audit-category" label={t('admin.audit.filter.category')} value={category} onChange={(e) => setCategory(e.target.value)} />
        </div>
        <div className="w-44">
          <TextInput id="admin-audit-action" label={t('admin.audit.filter.action')} value={action} onChange={(e) => setAction(e.target.value)} />
        </div>
        <div className="w-44">
          <TextInput
            id="admin-audit-from"
            label={t('admin.audit.filter.from')}
            type="date"
            value={occurredFrom}
            onChange={(e) => setOccurredFrom(e.target.value)}
          />
        </div>
        <div className="w-44">
          <TextInput
            id="admin-audit-to"
            label={t('admin.audit.filter.to')}
            type="date"
            value={occurredTo}
            onChange={(e) => setOccurredTo(e.target.value)}
          />
        </div>
        <div className="w-32">
          <Select
            id="admin-audit-limit"
            label={t('admin.audit.filter.limit')}
            value={String(limit)}
            onValueChange={(value) => setLimit(Number(value))}
            options={LIMIT_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
          />
        </div>
        <Button type="submit" variant="secondary">
          {t('admin.audit.filter.apply')}
        </Button>
      </form>

      {rows.length >= limit ? <AlertBanner variant="info" title={t('admin.audit.truncated', { limit })} /> : null}

      <DataTable
        caption={t('admin.audit.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        emptyTitle={t('admin.audit.empty.title')}
        emptyBody={t('admin.audit.empty.body')}
      />
    </div>
  );
}
